import {
  getIsChecklistPageEnabled,
  getIsTemplatesPageEnabled,
} from '../../../../pages/utils';

import { useIsSchedulerEnabledContext } from './use-is-scheduler-enabled.context';

/**
 * The checklist scheduler will be rolled out site by site. It is also only enabled for checklist admins and if the templates page is enabled.
 * This hook checks if the scheduler is enabled for the current user, project and location.
 *
 * @param flagKey The Unleash feature flag key for the scheduler feature.
 * @returns Boolean indicating whether the scheduler feature is enabled or not.
 */
export const useIsSchedulerEnabled = (
  flagKey: string
): { isEnabled: boolean; isLoading: boolean } => {
  const {
    useSelectedRootLocationConfiguration,
    useIsChecklistAdminQuery,
    useIsTemplateAdminQuery,
    useFeatureToggleConfig,
    useIsLocationEnabledForFeatureFlag,
  } = useIsSchedulerEnabledContext();

  const { data: isChecklistAdmin, isLoading: isChecklistAdminLoading } =
    useIsChecklistAdminQuery();
  const { data: isTemplateAdmin, isLoading: isTemplateAdminLoading } =
    useIsTemplateAdminQuery();

  const selectedRootLocation = useSelectedRootLocationConfiguration();
  const {
    config: rootLocationFeatureToggles,
    isLoading: isFeatureToggleConfigLoading,
  } = useFeatureToggleConfig(
    selectedRootLocation?.externalId ||
      selectedRootLocation?.assetExternalId ||
      '' // This is a workaround because the hook is not yet typed to accept undefined values
  );

  const isChecklistPageEnabled = getIsChecklistPageEnabled(
    rootLocationFeatureToggles
  );
  const isTemplatesPageEnabled = getIsTemplatesPageEnabled(
    rootLocationFeatureToggles
  );

  const {
    isEnabled: isCurrentLocationEnabledForScheduler,
    isLoading: isCurrentLocationEnabledForSchedulerLoading,
  } = useIsLocationEnabledForFeatureFlag(flagKey, selectedRootLocation);

  const isSchedulerEnabled =
    Boolean(isChecklistAdmin) &&
    Boolean(isTemplateAdmin) &&
    isChecklistPageEnabled &&
    isTemplatesPageEnabled &&
    isCurrentLocationEnabledForScheduler;

  const isLoading =
    isChecklistAdminLoading ||
    isTemplateAdminLoading ||
    isFeatureToggleConfigLoading ||
    isCurrentLocationEnabledForSchedulerLoading;

  return { isEnabled: isSchedulerEnabled && !isLoading, isLoading };
};
